import { ChevronDownIcon, SparklesIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { prefillNewConversationDraft, useAppDispatch } from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../../components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../../components/ui/dropdown-menu'

export interface ManualCreateOption {
  label: string
  route: string
}

export interface AiDraftOption {
  label: string
  promptTemplate: string
}

interface CreateConfigActionsProps {
  /**
   * Single template for a type-scoped Draft prompt with AI button.
   * Prefer `aiOptions` on the unified Configure list.
   */
  aiPromptTemplate?: string
  /** When set (and non-empty), Draft prompt with AI opens a type picker. */
  aiOptions?: readonly AiDraftOption[]
  /**
   * Single route for a type-scoped Create manually button
   * (for example, a dedicated create page). Prefer `manualOptions` on the
   * unified Configure list.
   */
  manualRoute?: string
  /** When set (and non-empty), Create manually opens a type picker. */
  manualOptions?: readonly ManualCreateOption[]
}

export function CreateConfigActions({
  aiPromptTemplate,
  aiOptions,
  manualRoute,
  manualOptions
}: CreateConfigActionsProps) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const draftPrompt = (template: string) => {
    dispatch(prefillNewConversationDraft(template))
    // The agent sidebar is collapsed by default below 1280px, so the
    // prefilled prompt is otherwise invisible — surface what just happened.
    toast('Prompt drafted in chat sidebar', {
      description: 'Open the chat sidebar on the right to review and send.'
    })
  }

  const manualMenu = manualOptions ?? []
  const showManualMenu = manualMenu.length > 0
  const showManualButton = !showManualMenu && Boolean(manualRoute)

  const aiMenu = aiOptions ?? []
  const showAiMenu = aiMenu.length > 0
  const showAiButton = !showAiMenu && Boolean(aiPromptTemplate)

  return (
    <>
      {showManualMenu && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1.5">
              Create manually
              <ChevronDownIcon className="size-3.5 text-ink-4" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[14rem] p-0.5">
            {manualMenu.map((opt) => (
              <DropdownMenuItem
                key={opt.route}
                className="cursor-pointer text-xs"
                onSelect={() => void navigate(opt.route)}
              >
                {opt.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {showManualButton && (
        <Button variant="outline" size="sm" onClick={() => void navigate(manualRoute!)}>
          Create manually
        </Button>
      )}
      {showAiMenu && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" className="gap-1.5">
              <SparklesIcon className="size-4" />
              Draft prompt with AI
              <ChevronDownIcon className="size-3.5 opacity-80" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[14rem] p-0.5">
            {aiMenu.map((opt) => (
              <DropdownMenuItem
                key={opt.label}
                className="cursor-pointer text-xs"
                onSelect={() => draftPrompt(opt.promptTemplate)}
              >
                {opt.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {showAiButton && (
        <Button size="sm" onClick={() => draftPrompt(aiPromptTemplate!)}>
          <SparklesIcon className="size-4" />
          Draft prompt with AI
        </Button>
      )}
    </>
  )
}
